/**
 * Vorlage „Abwiegen“.
 *
 * Eine Schüssel steht auf der Küchenwaage. Aus der Tüte rieselt Mehl hinein,
 * die Anzeige zählt mit, bis die gewünschte Menge erreicht ist.
 */
import { ease, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { compact, faded, group, label, line, rect, rotated, shape, styles } from '../scene.ts';
import { stream } from '../parts/kitchen.ts';
import { arrow, counter, doneMark, GROUND_Y } from '../parts/stage.ts';
import { bowl, interiorClip } from '../parts/vessels.ts';
import { gramsFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Waage. */
const SCALE = { cx: 104, width: 136, height: 28 } as const;
const PLATFORM_Y = GROUND_Y - SCALE.height - 5;
/** Schüssel auf der Waage. */
const BOWL = { width: 104, height: 36 } as const;
const BOWL_RIM = PLATFORM_Y - BOWL.height - 4;
/** Öffnung der Tüte. */
const OPENING = { x: 148, y: 56 } as const;

/** Zeitplan. */
const TIME = {
  bagIn: [0.02, 0.1],
  pour: [0.1, 0.7],
  bagOut: [0.72, 0.8],
  done: [0.72, 0.76],
  reset: [0.9, 1],
} as const;

/** Mehltüte, Öffnung im Ursprung, Körper nach oben. */
function bag(): ReturnType<typeof group> {
  const body = roundedRectPath(-17, -50, 34, 50, 3);
  return group([
    shape(body, styles.solid('paper')),
    rect(-17, -34, 34, 14, 0, { fill: 'shade', stroke: 'none' }),
    shape(body, styles.outline()),
    line(-17, -6, 17, -6, styles.fine('deep')),
  ]);
}

/** Anzeige: ganze Gramm, ab 1000 mit Tausenderpunkt. */
function gramsLabel(grams: number): string {
  const rounded = Math.round(grams);
  return `${rounded >= 1000 ? rounded.toLocaleString('de-DE') : rounded} g`;
}

/** Vorlage „Abwiegen“. */
export const prepWeighTemplate = defineTemplate({
  id: 'prep.weigh',
  group: 'prep',
  title: 'Abwiegen',
  description:
    'Die Schüssel steht auf der Küchenwaage. Die Zutat wird langsam hineingegeben, bis die Anzeige die gewünschte Menge zeigt.',
  durationSeconds: 6,
  keywords: ['abwiegen', 'abwägen', 'wiegen', 'küchenwaage', 'waage', 'gramm'],
  stillTime: 0.74,
  parameters: [
    {
      kind: 'number',
      name: 'grams',
      label: 'Menge',
      min: 1,
      max: 5000,
      step: 5,
      unit: 'g',
      defaultValue: 250,
    },
  ],
  inferValues(text) {
    return { grams: gramsFromText(text) };
  },
  scene(values, t) {
    const target = numberValue(values, 'grams', 250);
    const vessel = bowl(SCALE.cx, BOWL_RIM, BOWL.width, BOWL.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const pour = segment(t, ...TIME.pour);
    const amount = ease.inOutSine(pour) * (1 - reset);
    const flow =
      segment(t, TIME.pour[0], TIME.pour[0] + 0.04) * (1 - segment(t, TIME.pour[1] - 0.04, TIME.pour[1]));
    const bagShown =
      ease.inOutCubic(segment(t, ...TIME.bagIn)) * (1 - ease.inOutCubic(segment(t, ...TIME.bagOut)));
    const tilt = 40 + 22 * Math.sin(pour * Math.PI);
    const moundTop = BOWL_RIM + 14 - amount * 26;
    const mound = path()
      .move(SCALE.cx - 44, BOWL_RIM + 24)
      .curve(SCALE.cx - 26, moundTop, SCALE.cx + 22, moundTop, SCALE.cx + 44, BOWL_RIM + 24)
      .close()
      .toString();
    const done = segment(t, ...TIME.done) * (1 - segment(t, 0.88, 0.9));

    const display = { x: SCALE.cx - 38, y: GROUND_Y - SCALE.height + 4, width: 76, height: 20 };
    return compact([
      ...counter(),
      // Waage: Körper, Wiegefläche, Anzeige
      shape(
        roundedRectPath(SCALE.cx - SCALE.width / 2, GROUND_Y - SCALE.height, SCALE.width, SCALE.height, 7),
        styles.solid('paper'),
      ),
      rect(SCALE.cx - SCALE.width / 2 + 8, PLATFORM_Y, SCALE.width - 16, 5, 2, styles.solid('shade')),
      rect(display.x, display.y, display.width, display.height, 4, {
        fill: 'cavity',
        stroke: 'deep',
        strokeWidth: STROKE.fine,
      }),
      label(display.x + display.width / 2, display.y + 15, gramsLabel(target * amount), 15, {
        fill: 'snow',
        weight: 800,
      }),
      ...vessel.back,
      amount > 0.01 ? group([shape(mound, styles.snow())], { clip: interiorClip(vessel, 30) }) : null,
      ...vessel.front,
      flow > 0.02
        ? stream(
            [OPENING, { x: OPENING.x - 16, y: OPENING.y + 18 }, { x: SCALE.cx + 8, y: moundTop + 2 }],
            7 * flow,
            t,
            'snow',
          )
        : null,
      faded(bagShown, [
        rotated(tilt, OPENING.x, OPENING.y, [group([bag()], { transform: { x: OPENING.x, y: OPENING.y } })]),
      ]),
      faded(ease.outBack(done), [
        doneMark(SCALE.cx + SCALE.width / 2 + 14, GROUND_Y - SCALE.height / 2, 1.1),
      ]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 196, y: 30 },
        { x: 160, y: 46 },
        { x: 124, y: 72 },
      ]),
    ];
  },
});
